"use client";

import {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
} from "react";
import { useCurrentUser } from "./use-current-user";
import { api } from "@/lib/api-client";
import {
  migrateMarketplaceWorkspace,
  getMarketplaceWorkspaceSnapshot,
  rememberedMarketplacePath,
  subscribeMarketplaceWorkspace,
} from "@/lib/marketplace-workspace";

export const ApiWorkspaceActorContext = createContext<
  (id: string | null) => void
>(() => {});
export const useApiWorkspaceActor = () => useContext(ApiWorkspaceActorContext);
export const MarketplaceWorkspaceSnapshotContext = createContext("");
export const MarketplaceWorkspacePersistenceContext = createContext(true);

export function useRememberedMarketplacePath(
  actorUserId?: string | null,
  initialSnapshot?: string,
  enabled = true,
) {
  const serverSnapshot = useContext(MarketplaceWorkspaceSnapshotContext);
  const persistenceEnabled =
    useContext(MarketplaceWorkspacePersistenceContext) && enabled;
  useEffect(() => {
    if (persistenceEnabled) migrateMarketplaceWorkspace(actorUserId);
  }, [actorUserId, persistenceEnabled]);
  const snapshot = useSyncExternalStore(
    subscribeMarketplaceWorkspace,
    () => (persistenceEnabled ? getMarketplaceWorkspaceSnapshot() : ""),
    () => (persistenceEnabled ? (initialSnapshot ?? serverSnapshot) : ""),
  );
  return rememberedMarketplacePath(snapshot, actorUserId);
}

export function useMarketplaceWorkspace(professional?: boolean) {
  const { user } = useCurrentUser();
  const rememberedPath = useRememberedMarketplacePath(user?.id);
  const query = api.os.workspace.getMarketplaceContext.useQuery(undefined, {
    enabled: Boolean(
      user && (professional ?? (user.isAgent || rememberedPath)),
    ),
    staleTime: 5_000,
    retry: false,
  });
  return { ...query, rememberedPath };
}
